import { cn } from "../../lib/utils";
import React, { useEffect, useRef, useState } from "react";
import { createNoise3D } from "simplex-noise";

export const WavyBackgroundComponent = ({
  children,
  className,
  containerClassName,
  colors,
  waveWidth,
  backgroundFill = "white",
  blur = 10,
  speed = "fast",
  waveOpacity = 0.5,
  ...props
}) => {
  const [isClient, setIsClient] = useState(false);
  
  const noise = createNoise3D();
  const varsRef = useRef({
    w: 0,
    h: 0,
    nt: 0,
    ctx: null,
    canvas: null,
    animationId: null
  });
  const canvasRef = useRef(null);
  
  const getSpeed = () => {
    switch (speed) {
      case "slow":
        return 0.001;
      case "fast":
        return 0.002;
      default:
        return 0.001;
    }
  };

  const init = () => {
    if (!isClient) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    
    varsRef.current.canvas = canvas;
    varsRef.current.ctx = ctx;
    varsRef.current.w = ctx.canvas.width = canvas.offsetWidth;
    varsRef.current.h = ctx.canvas.height = canvas.offsetHeight;
    varsRef.current.nt = 0;
    
    render();
  };

  const waveColors = colors ?? [
    "#38bdf8",
    "#818cf8",
    "#c084fc",
    "#e879f9",
    "#22d3ee",
  ];
  
  const drawWave = (n) => {
    const { ctx, w, h } = varsRef.current;
    if (!ctx) return;
    varsRef.current.nt += getSpeed();
    for (let i = 0; i < n; i++) {
      ctx.beginPath();
      ctx.lineWidth = waveWidth || 50;
      ctx.strokeStyle = waveColors[i % waveColors.length];
      for (let x = 0; x < w; x += 5) {
        var y = noise(x / 800, 0.3 * i, varsRef.current.nt) * 100;
        ctx.lineTo(x, y + h * 0.5); // adjust for height, currently at 50% of the container
      }
      ctx.stroke();
      ctx.closePath();
    }
  };

  const render = () => {
    const { ctx, w, h } = varsRef.current;
    if (!ctx) return;
    ctx.fillStyle = backgroundFill || "black";
    ctx.globalAlpha = waveOpacity || 0.5;
    ctx.fillRect(0, 0, w, h);
    drawWave(5);
    varsRef.current.animationId = requestAnimationFrame(render);
  };

  useEffect(() => {
    const vars = varsRef.current;
    if (isClient) {
      init();
    }
    
    const handleResize = () => {
      const { canvas, ctx } = vars;
      if (!canvas || !ctx) return;
      // Only resize if width changes (prevents mobile scroll glitch)
      const newW = canvas.offsetWidth;
      const newH = canvas.offsetHeight;
      
      if (newW !== vars.w) {
        vars.w = ctx.canvas.width = newW;
        vars.h = ctx.canvas.height = newH;
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (vars.animationId) {
        cancelAnimationFrame(vars.animationId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClient, blur, backgroundFill, waveOpacity, speed, waveWidth, colors]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  return (
    <div
      className={cn(
        "relative w-full h-full flex flex-col items-center justify-center",
        containerClassName
      )}
    >
      <canvas
        className="absolute inset-0 z-0 w-full h-full"
        ref={canvasRef}
        id="canvas"
        style={{
          filter: `blur(${blur}px)`,
        }}
      />
      <div className={cn("relative z-10", className)} {...props}>
        {children}
      </div>
    </div>
  );
};

export const WavyBackground = React.memo(WavyBackgroundComponent);
